'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Article } from '@/types/sanity';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ArrowUpDown, Play } from 'lucide-react';
import { urlFor } from '@/sanity/lib/image';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/utils/date';

const ARTICLES_PER_PAGE = 12;

interface NewsPageClientProps {
  articles: Article[];
}

export function NewsPageClient({ articles }: NewsPageClientProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);

  const categories = useMemo(() => {
    const cats = new Set(articles.map((a) => a.category).filter(Boolean) as string[]);
    return Array.from(cats).sort();
  }, [articles]);

  const categoryLabels: Record<string, string> = {
    'company-news': 'Company News',
    'product-updates': 'Product Updates',
    'industry-insights': 'Industry Insights',
    'case-studies': 'Case Studies',
    events: 'Events',
  };

  const filteredAndSortedArticles = useMemo(() => {
    let result = articles;

    if (selectedCategory !== 'all') {
      result = result.filter((article) => article.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          (article.excerpt && article.excerpt.toLowerCase().includes(query))
      );
    }

    result = [...result].sort((a, b) => {
      const dateA = new Date(a.publishedAt || 0).getTime();
      const dateB = new Date(b.publishedAt || 0).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [articles, searchQuery, selectedCategory, sortOrder]);

  const totalPages = Math.ceil(filteredAndSortedArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = filteredAndSortedArticles.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'));
    setCurrentPage(1);
  };

  return (
    <section className="py-8 md:py-12 lg:py-16">
      <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
        <div className="px-6 sm:px-0">
          {/* Header */}
          <h1 className="font-medium mb-4 text-section-title leading-section-title">H3D News</h1>
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={handleSortToggle} className="gap-2 shrink-0">
              <ArrowUpDown className="h-4 w-4" />
              {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
            </Button>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleCategoryChange('all')}
              >
                All
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCategoryChange(cat)}
                >
                  {categoryLabels[cat] || cat}
                </Button>
              ))}
            </div>
          )}

          {/* Articles Grid */}
          {paginatedArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">
              {paginatedArticles.map((article) => (
                <Link
                  key={article._id}
                  href={`/news/${article.slug.current}`}
                  className="group block"
                >
                  <div className="aspect-video relative rounded-xl overflow-hidden bg-muted mb-3">
                    {article.image?.asset ? (
                      <Image
                        src={urlFor(article.image).width(640).height(360).url()}
                        alt={article.image?.alt || article.title}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                        No image
                      </div>
                    )}
                    {article.videoUrl && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90">
                          <Play className="h-5 w-5 text-foreground fill-foreground ml-0.5" />
                        </div>
                      </div>
                    )}
                  </div>
                  <h3 className="font-medium text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {article.author && <span>{article.author}</span>}
                    {article.author && article.publishedAt && <span> · </span>}
                    {article.publishedAt && (
                      <time dateTime={article.publishedAt}>
                        {formatTimeAgo(article.publishedAt)}
                      </time>
                    )}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">
                {searchQuery || selectedCategory !== 'all'
                  ? 'No articles found matching your filters.'
                  : 'No articles available yet.'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="flex items-center justify-center gap-2 mt-12" aria-label="Pagination">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={page === currentPage ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className={cn('min-w-[2.25rem]', page === currentPage && 'pointer-events-none')}
                >
                  {page}
                </Button>
              ))}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </nav>
          )}
        </div>
      </div>
    </section>
  );
}
